// frontend/src/components/public/blocks/MissionVisionBlock.tsx
import React from 'react';
import { Box, Typography, Grid, Card, CardContent } from '@mui/material';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface MissionVisionBlockProps {
  data: {
    mission: string;
    vision: string;
    values: string[] | string;
    artisticVision?: string;
    communityCommitment?: string;
    layout: 'stacked' | 'side-by-side';
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const MissionVisionBlock: React.FC<MissionVisionBlockProps> = ({ data, theme }) => {
  const {
    mission,
    vision,
    values: rawValues = [],
    artisticVision,
    communityCommitment,
    layout = 'stacked'
  } = data;

  // Convert values from string (textarea) to array
  const values: string[] = Array.isArray(rawValues) 
    ? rawValues 
    : typeof rawValues === 'string' 
      ? rawValues.split('\n').filter((v: string) => v.trim())
      : [];

  const primaryColor = theme?.primaryColor || '#000000';
  const accentColor = theme?.accentColor || '#FF0000';

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 3, md: 6 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Mission & Vision Section */}
      <Grid container spacing={4}>
        {layout === 'side-by-side' ? (
          <>
            {/* Side by Side Layout */}
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${accentColor}08 0%, ${accentColor}15 100%)`,
                  border: `1px solid ${accentColor}30`,
                  borderRadius: 3,
                  p: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${accentColor}25`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${accentColor} 0%, ${primaryColor} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <TrackChangesIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '1.75rem', md: '2.25rem' },
                      fontWeight: 700,
                      color: primaryColor
                    }}
                  >
                    Our Mission
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    lineHeight: 1.8,
                    color: '#333333'
                  }}
                >
                  {mission}
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  background: `linear-gradient(135deg, ${accentColor}08 0%, ${accentColor}15 100%)`,
                  border: `1px solid ${accentColor}30`,
                  borderRadius: 3,
                  p: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${accentColor}25`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${accentColor} 0%, ${primaryColor} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <VisibilityIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '1.75rem', md: '2.25rem' },
                      fontWeight: 700,
                      color: primaryColor
                    }}
                  >
                    Our Vision
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    lineHeight: 1.8,
                    color: '#333333'
                  }}
                >
                  {vision}
                </Typography>
              </Card>
            </Grid>
          </>
        ) : (
          <>
            {/* Stacked Layout */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${accentColor}08 0%, ${accentColor}15 100%)`,
                  border: `1px solid ${accentColor}30`,
                  borderRadius: 3,
                  p: 4,
                  mb: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${accentColor}25`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${accentColor} 0%, ${primaryColor} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <TrackChangesIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '1.75rem', md: '2.25rem' },
                      fontWeight: 700,
                      color: primaryColor
                    }}
                  >
                    Our Mission
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    lineHeight: 1.8,
                    color: '#333333'
                  }}
                >
                  {mission}
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${accentColor}08 0%, ${accentColor}15 100%)`,
                  border: `1px solid ${accentColor}30`,
                  borderRadius: 3,
                  p: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${accentColor}25`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${accentColor} 0%, ${primaryColor} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <VisibilityIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '1.75rem', md: '2.25rem' },
                      fontWeight: 700,
                      color: primaryColor
                    }}
                  >
                    Our Vision
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '1rem', md: '1.125rem' },
                    lineHeight: 1.8,
                    color: '#333333'
                  }}
                >
                  {vision}
                </Typography>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Decorative Divider */}
      {values.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', my: 8 }}>
          <Box sx={{ flex: 1, height: '2px', background: `linear-gradient(to right, transparent, ${accentColor}50, transparent)` }} />
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: accentColor,
              mx: 2
            }}
          />
          <Box sx={{ flex: 1, height: '2px', background: `linear-gradient(to right, transparent, ${accentColor}50, transparent)` }} />
        </Box>
      )}

      {/* Values Section */}
      {values.length > 0 && (
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              mb: 5,
              color: primaryColor,
              textAlign: 'center'
            }}
          >
            Our Values
          </Typography>

          <Grid container spacing={3}>
            {values.map((value: string, index: number) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    background: `linear-gradient(135deg, ${primaryColor}15 0%, ${accentColor}25 100%)`,
                    border: `2px solid ${accentColor}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 32px ${accentColor}40`,
                      background: `linear-gradient(135deg, ${primaryColor}25 0%, ${accentColor}35 100%)`
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: primaryColor,
                        fontSize: '1.125rem'
                      }}
                      component="div"
                      dangerouslySetInnerHTML={{ __html: value || '' }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Artistic Vision (Optional) */}
      {artisticVision && (
        <Box sx={{ mt: 8 }}>
          <Card
            elevation={0}
            sx={{
              background: `linear-gradient(135deg, ${accentColor}08 0%, ${accentColor}15 100%)`,
              border: `1px solid ${accentColor}30`,
              borderRadius: 3,
              p: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: `0 12px 32px ${accentColor}25`
              }
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                fontWeight: 700,
                mb: 3,
                color: primaryColor
              }}
            >
              Our Artistic Vision
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                color: '#333333'
              }}
            >
              {artisticVision}
            </Typography>
          </Card>
        </Box>
      )}

      {/* Community Commitment (Optional) */}
      {communityCommitment && (
        <Box sx={{ mt: 4 }}>
          <Card
            elevation={0}
            sx={{
              background: `linear-gradient(135deg, ${accentColor}08 0%, ${accentColor}15 100%)`,
              border: `1px solid ${accentColor}30`,
              borderRadius: 3,
              p: 4,
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: `0 12px 32px ${accentColor}25`
              }
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                fontWeight: 700,
                mb: 3,
                color: primaryColor
              }}
            >
              Our Community Commitment
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                color: '#333333'
              }}
            >
              {communityCommitment}
            </Typography>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default MissionVisionBlock;